import { type MutableRefObject, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type Webcam from 'react-webcam';
import { sleep } from '@utils/util';

import { VerificationStatusEnum } from '../types';

const PREPARE_SECONDS = 5;

type UseLivenessPrepareCountdownParams = {
    webcamRef: RefObject<Webcam | null>;
    isPaused: MutableRefObject<boolean>;
    isMaskReady: boolean;
    isTfliteScriptReady: boolean;
    areFaceModelsLoaded: boolean;
    hasFaceDetectorLoadError: boolean;
    status: VerificationStatusEnum | null;
    setStatus: (status: VerificationStatusEnum) => void;
    detect: () => Promise<unknown>;
};

export const useLivenessPrepareCountdown = ({
    webcamRef,
    isPaused,
    isMaskReady,
    isTfliteScriptReady,
    areFaceModelsLoaded,
    hasFaceDetectorLoadError,
    status,
    setStatus,
    detect,
}: UseLivenessPrepareCountdownParams) => {
    const [prepareSeconds, setPrepareSeconds] = useState<number>(PREPARE_SECONDS);
    const prepareSecondsRef = useRef(prepareSeconds);
    const prepareGenerationRef = useRef(0);
    const initialPrepareDoneRef = useRef(false);

    useEffect(() => {
        prepareSecondsRef.current = prepareSeconds;
    }, [prepareSeconds]);

    const abortPrepare = useCallback(() => {
        prepareGenerationRef.current += 1;
    }, []);

    const prepare = useCallback(async () => {
        const generation = prepareGenerationRef.current;

        setPrepareSeconds(PREPARE_SECONDS);
        let seconds = PREPARE_SECONDS;

        while (seconds > 0) {
            if (prepareGenerationRef.current !== generation) {
                return;
            }

            webcamRef.current?.getScreenshot();
            // eslint-disable-next-line no-await-in-loop
            await sleep(1000);

            if (prepareGenerationRef.current !== generation) {
                return;
            }

            seconds -= 1;
            setPrepareSeconds(seconds);
        }

        isPaused.current = false;
    }, [webcamRef, isPaused]);

    const skipPrepareCountdown = useCallback(() => {
        prepareGenerationRef.current += 1;
        initialPrepareDoneRef.current = true;
        prepareSecondsRef.current = 0;
        setPrepareSeconds(0);
        isPaused.current = false;
        setStatus(VerificationStatusEnum.READY);
    }, [isPaused, setStatus]);

    useEffect(() => {
        if (hasFaceDetectorLoadError) {
            setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

            return;
        }

        if (
            !isMaskReady ||
            !isTfliteScriptReady ||
            !areFaceModelsLoaded ||
            initialPrepareDoneRef.current ||
            status === VerificationStatusEnum.SUCCESS
        ) {
            return;
        }

        initialPrepareDoneRef.current = true;

        const ready = async () => {
            setStatus(VerificationStatusEnum.PREPARING);
            // one detect to make load face detector and prevent lag
            await Promise.all([prepare(), detect()]);
            setStatus(VerificationStatusEnum.READY);
        };

        ready().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMaskReady, isTfliteScriptReady, areFaceModelsLoaded, hasFaceDetectorLoadError]);

    return {
        prepareSeconds,
        prepareSecondsRef,
        prepare,
        skipPrepareCountdown,
        abortPrepare,
    };
};
